/**
 * Naming the offer the buyer actually chose, at checkout (#2104).
 *
 * An offer has no id of its own. Checkout posts `productId: "65.3"` and the
 * server dereferences `product.offers[3]`, so the index IS the identity — and the
 * public catalogue endpoint hands back an array with the hidden offers REMOVED,
 * while every checkout path re-reads the product with them still in. Any product
 * with a hidden offer ahead of a visible one therefore checked out as a different
 * offer at a different price.
 *
 * Two pieces close that:
 *
 *   • `offerCheckoutIndex` — the server now stamps `_index` (the offer's true
 *     position in the product's own offers array) on every catalogue offer, so
 *     the client stops sending its own array position.
 *   • `offerFingerprint` — the identity of the offer as rendered, sent alongside.
 *     The server refuses the purchase when it does not match what the index
 *     resolves to, so a catalogue that moved between page load and checkout errors
 *     rather than quietly charging something else.
 *
 * The server mirror is `server/src/shared/offerCatalog.js`. The two MUST agree on
 * the fingerprint format; both test suites assert the same golden strings against
 * the shared production offer fixtures.
 */

type OfferLike = Partial<API.MembershipProductOffer> & { _index?: number };

const num = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value);

/**
 * The index to post for this offer: its true position in `product.offers` when
 * the catalogue supplied one, else the caller's own array position.
 *
 * The fallback exists for offers that never came from a catalogue read (tests,
 * and any admin surface building an order by hand). It is a fallback, not a
 * default — a public product page offer always carries `_index`.
 */
export function offerCheckoutIndex(offer: OfferLike | undefined, fallbackIndex: number) {
  const stamped = offer?._index;
  return typeof stamped === 'number' && Number.isInteger(stamped) && stamped >= 0
    ? stamped
    : fallbackIndex;
}

/**
 * The fields that say WHICH offer this is, as a stable delimited string: the
 * services granted, the play count, the price, and the package it is sold as.
 *
 * Deliberately narrow, and deliberately not `ui.title` — per-user personalization
 * stamps `winback` / `upgrade` / `_index` onto an offer and the order page adds
 * nothing else, so this string is identical to the one the server computes from
 * the catalogue row. `ids` is sorted because it is a SET of service ids: a
 * cosmetic reorder in the admin must not lock a buyer out of checkout.
 */
export function offerFingerprint(offer: OfferLike | undefined | null): string | null {
  if (!offer || typeof offer !== 'object') {
    return null;
  }

  const id = num(offer.id);
  const ids = Array.isArray(offer.ids)
    ? offer.ids
        .map(num)
        .filter((value): value is number => value !== null)
        .sort((a, b) => a - b)
    : null;

  return [
    id === null ? '' : id,
    ids === null ? '' : ids.join(','),
    num(offer.plays) ?? '',
    num(offer.price) ?? '',
    offer.ui?.packageKey === null || offer.ui?.packageKey === undefined
      ? ''
      : String(offer.ui.packageKey),
  ].join('|');
}

/** The server's codes for "that is not the offer you were shown" (#2104). */
export const OFFER_MISMATCH_ERROR = 'ORDER_OFFER_MISMATCH';
export const OFFER_UNAVAILABLE_ERROR = 'ORDER_OFFER_UNAVAILABLE';

/**
 * The catalogue moved under the buyer, so the server refused rather than charging
 * a different offer. Unlike the duplicate prompt this is NOT retryable with a
 * flag — the page is stale and has to be reloaded.
 */
export function isOfferResolutionError(error: unknown) {
  const code = (error as { data?: { error?: string } })?.data?.error;
  return code === OFFER_MISMATCH_ERROR || code === OFFER_UNAVAILABLE_ERROR;
}
